import React from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  getNotificationContent,
  getNotificationIcon,
  getNotificationSeverityClass,
} from "./notificationUtils";

const LANGUAGE_LOCALES = {
  en: "en-GB",
  es: "es-ES",
  ca: "ca-ES",
  nl: "nl-BE",
};

export default function NotificationItem({
  notification = {},
  selectable = false,
  selected = false,
  onToggleSelect,
  onMarkRead,
  onClose,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("notifications");

  const content = getNotificationContent(notification, t);

  const Icon = getNotificationIcon(notification.notification_type);

  const severityClass = getNotificationSeverityClass(notification.severity);

  const formattedCreatedAt = React.useMemo(() => {
    if (!notification.created_at) {
      return notification.created_at_display || "";
    }

    const date = new Date(notification.created_at);

    if (Number.isNaN(date.getTime())) {
      return notification.created_at_display || "";
    }

    const currentLanguage = (
      i18n.resolvedLanguage ||
      i18n.language ||
      "en"
    ).split("-")[0];

    const locale = LANGUAGE_LOCALES[currentLanguage] || "en-GB";

    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  }, [
    notification.created_at,
    notification.created_at_display,
    i18n.language,
    i18n.resolvedLanguage,
  ]);

  async function handleClick() {
    if (selectable) {
      onToggleSelect?.();
      return;
    }

    try {
      if (!notification.is_read) {
        await onMarkRead?.(notification);
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }

    if (notification.action_url) {
      navigate(notification.action_url);
    }

    onClose?.();
  }

  return (
    <button
      type="button"
      className={[
        "notification-card",
        severityClass,
        notification.is_read ? "" : "unread",
        selectable ? "selection-mode" : "",
        selected ? "is-selected" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      aria-pressed={selectable ? selected : undefined}
    >
      {selectable ? (
        <div
          className={["notification-card__checkbox", selected ? "selected" : ""]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          {selected ? <Check size={14} /> : null}
        </div>
      ) : null}

      <div className="notification-card__icon" aria-hidden="true">
        <Icon size={18} />
      </div>

      <div className="notification-card__content">
        <div className="notification-card__heading">
          <strong className="notification-card__title">{content.title}</strong>

          {!notification.is_read ? (
            <span
              className="notification-card__unread-dot"
              aria-label={t("messages.unread", "Unread")}
            />
          ) : null}
        </div>

        {content.body ? (
          <p className="notification-card__body">{content.body}</p>
        ) : null}

        {content.context ? (
          <p className="notification-card__context">{content.context}</p>
        ) : null}

        {content.preview ? (
          <p className="notification-card__preview">{content.preview}</p>
        ) : null}

        {content.meta?.length > 0 ? (
          <div className="notification-card__meta">
            {content.meta.map((item) => (
              <span
                key={`${item.label}-${item.value}`}
                className="notification-card__meta-item"
              >
                <span className="notification-card__meta-label">
                  {item.label}
                </span>

                <strong>{item.value}</strong>
              </span>
            ))}
          </div>
        ) : null}

        <div className="notification-card__footer">
          {content.time ? (
            <span className="notification-card__time">{content.time}</span>
          ) : null}

          {formattedCreatedAt ? (
            <span className="notification-card__date">
              {formattedCreatedAt}
            </span>
          ) : null}
        </div>
      </div>

      {!notification.is_read && !selectable ? (
        <span className="notification-card__dot" aria-hidden="true" />
      ) : null}
    </button>
  );
}
